import { Redirect } from "@redotech/react-router-util/redirect";
import { useRequiredContext } from "@redotech/react-util/context";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo } from "react";
import {
  matchPath,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";

const disputeDetailBreadcrumb = breadcrumbSlot();

/** Lands chargeback merchants on their slice of the hub, not the full catalog. */
function ChargebacksIntegrationsRedirect() {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("products", "reclaim");
  const next = pathname.replace(
    /payments\/chargebacks\/integrations/,
    "settings/integrations",
  );
  return <Navigate replace to={`${next}?${params.toString()}`} />;
}

const HIDE_PADDING_ROUTES: string[] = [
  "/chargebacks/overview",
  "/chargebacks/reclaim",
  "/chargebacks/reclaim/*",
  "/chargebacks/alerts",
  "/chargebacks/alerts/*",
  "/chargebacks/resolve",
  "/chargebacks/resolve/*",
  "/chargebacks/:disputeId",
];

//redontIgnore -- shared hide-padding shape every route file repeats by convention
function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/payments${route}`, url),
  );
}

function DisputeDetailLegacyRedirect() {
  const { disputeId } = useParams<{ disputeId: string }>();

  return (
    <Navigate
      replace
      to={
        disputeId ? `../reclaim/${encodeURIComponent(disputeId)}` : "../reclaim"
      }
    />
  );
}

/** HSA-only merchants land on the HSA dashboard instead of the Reclaim upsell. */
export function paymentsDefaultPath(settings: {
  disputes?: { enabled?: boolean };
  hsa?: { enabled?: boolean };
}): "chargebacks" | "hsa" {
  if (!settings.disputes?.enabled && settings.hsa?.enabled) {
    return "hsa";
  }
  return "chargebacks";
}

const PaymentsDefaultRoute = memo(function PaymentsDefaultRoute() {
  const team = useRequiredContext(TeamContext);

  return <Navigate replace to={paymentsDefaultPath(team.settings)} />;
});

const HsaEnabledGate = memo(function HsaEnabledGate() {
  const team = useRequiredContext(TeamContext);

  if (!team.settings.hsa?.enabled) {
    return <Navigate replace to="../chargebacks" />;
  }
  return <Outlet />;
});

export const paymentsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Payments" },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <Page hidePadding={hidePadding} /> };
    },
    children: [
      { index: true, element: <PaymentsDefaultRoute /> },
      {
        path: "hsa",
        handle: { breadcrumb: "HSA/FSA payments" },
        element: <HsaEnabledGate />,
        children: [
          {
            index: true,
            async lazy() {
              const { HsaAnalyticsPage } =
                await import("../../../payments/hsa/hsa-analytics-page");
              return { Component: HsaAnalyticsPage };
            },
          },
        ],
      },
      {
        path: "chargebacks",
        handle: { breadcrumb: "Chargebacks" },
        async lazy() {
          const { DisputesEnabledGate } =
            await import("../../../payments/chargebacks/disputes-enabled-gate");
          return {
            element: (
              <DisputesEnabledGate>
                <Outlet />
              </DisputesEnabledGate>
            ),
          };
        },
        children: [
          { index: true, element: <Navigate replace to="overview" /> },
          {
            path: "overview",
            handle: { breadcrumb: "Overview" },
            async lazy() {
              const { ChargebacksOverviewRoute } =
                await import("../../../payments/chargebacks/overview/chargebacks-overview-page");
              return { Component: ChargebacksOverviewRoute };
            },
          },
          {
            path: "reclaim",
            handle: { breadcrumb: "Reclaim" },
            children: [
              {
                index: true,
                async lazy() {
                  const { DisputesPage } =
                    await import("../../../payments/chargebacks/disputes-page");
                  return { Component: DisputesPage };
                },
              },
              {
                path: ":disputeId",
                handle: { breadcrumb: disputeDetailBreadcrumb },
                async lazy() {
                  const { DisputeDetailPage } =
                    await import("../../../payments/chargebacks/dispute-detail-page");
                  return {
                    element: (
                      <DisputeDetailPage breadcrumb={disputeDetailBreadcrumb} />
                    ),
                  };
                },
              },
            ],
          },
          {
            path: "alerts",
            handle: { breadcrumb: "Alerts" },
            children: [
              {
                index: true,
                async lazy() {
                  const { DisputeAlertsPage } =
                    await import("../../../payments/chargebacks/alerts/dispute-alerts-page");
                  return { Component: DisputeAlertsPage };
                },
              },
              {
                path: ":alertId",
                handle: { breadcrumb: "Alert details" },
                async lazy() {
                  const { AlertDetailPage } =
                    await import("../../../payments/chargebacks/alerts/alert-detail-page");
                  return { Component: AlertDetailPage };
                },
              },
            ],
          },
          {
            path: "resolve",
            handle: { breadcrumb: "Resolve" },
            children: [
              {
                index: true,
                async lazy() {
                  const { ResolveDeflectionsPage } =
                    await import("../../../payments/chargebacks/resolve/resolve-deflections-page");
                  return { Component: ResolveDeflectionsPage };
                },
              },
              {
                path: ":deflectionId",
                handle: { breadcrumb: "Resolve request" },
                async lazy() {
                  const { ResolveDeflectionDetailPage } =
                    await import("../../../payments/chargebacks/resolve/resolve-deflection-detail-page");
                  return { Component: ResolveDeflectionDetailPage };
                },
              },
            ],
          },
          {
            path: "settings",
            element: (
              <Redirect
                match={/payments\/chargebacks\/settings/}
                replaceValue="settings/chargebacks/reclaim"
              />
            ),
          },
          {
            path: "integrations",
            element: <ChargebacksIntegrationsRedirect />,
          },
          { path: ":disputeId", element: <DisputeDetailLegacyRedirect /> },
        ],
      },
    ],
  },
];
